/* eslint-disable camelcase */
import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IDataNoPagination } from '../types/shared';

// Espelha VeredictoFidelidade/RelatorioFidelidade do back (auditor de fidelidade
// societario v3 — Onda F). Read-only: o auditor SO SINALIZA, o painel apenas
// exibe os vereditos ja persistidos. Endpoints admin/dev (back 403 p/ os outros).

export type ClassificacaoFidelidade = 'legitima' | 'suspeita' | 'vestigial';

export interface IVeredictoFidelidade {
  clausula_numero: number;
  classificacao: ClassificacaoFidelidade;
  motivo: string;
  // Comparativo lado-a-lado (vazio em relatorios antigos nao re-enriquecidos).
  texto_vigente: string;
  texto_render: string;
}

// Camada 1 F2 (2026-07-03): mapa COMPLETO das cláusulas (todas, não só as
// divergências) — o operador vê o que veio do vigente vs o que foi gerado.
export type OrigemClausula = 'preservada' | 'substituida' | 'gerada' | 'corpo';
export type StatusClausulaAudit = 'identica' | 'divergente' | 'nova' | 'assinatura';

// POR QUE divergiu. Campo ADITIVO (2026-08-11) — o back nao criou valor novo em
// `status` justamente porque o enum acima e' tipado de forma exaustiva aqui.
// Relatorio antigo (ou back desatualizado) nao manda o campo: `undefined` cai no
// mesmo caminho de `null` = divergencia de conteudo.
//
//   null | undefined  divergencia de conteudo — a unica que merece atencao
//   'concordancia'    casa com regra registrada (uni<->pluri, genero)
//   'formatacao'      difere so em espaco/quebra de linha
export type MotivoDivergencia = 'concordancia' | 'formatacao';

export interface IClausulaAuditada {
  numero: number;
  titulo: string;
  parte: string | null; // "A" | "B" | "constituicao"
  origem: OrigemClausula;
  status: StatusClausulaAudit;
  motivo_divergencia?: MotivoDivergencia | null;
  // `regra_padrao` das regras que explicam a divergencia (uni_para_pluri_*,
  // pluri_uni:*, din_*, pol_*). Vem preenchido mesmo quando o motivo e' null —
  // aí significa "concordancia + algo mais", e o algo mais e' que importa.
  regras_concordancia?: string[];
  texto_vigente: string;
  texto_render: string;
}

export interface IEstruturaAuditada {
  clausulas_sumidas: number[];
}

// Fase 2 (issue #1158): parecer no formato da issue — sintese dos buckets +
// status (aprovado | necessita_revisao) justificado. Derivado (deterministico)
// do mesmo julgamento, computado ON-READ (so no endpoint por-contrato).
export type StatusParecer = 'aprovado' | 'necessita_revisao';

export interface IItemParecer {
  clausula_numero: number;
  parte: string | null;
  titulo: string;
  descricao: string;
}

export interface IParecerValidacao {
  status: StatusParecer;
  justificativa: string;
  alteracoes: IItemParecer[];
  removidas: IItemParecer[];
  adicionadas: IItemParecer[];
  inconsistencias: IItemParecer[];
  pendencias: IItemParecer[];
}

export interface IAuditoriaFidelidade {
  id: number;
  dt_criacao: string;
  contrato_id: number;
  numero_alteracao: number | null;
  modelo_claude: string | null;
  total_clausulas_preservadas: number;
  total_divergencias: number;
  total_suspeitas: number;
  total_vestigiais: number;
  vereditos: IVeredictoFidelidade[];
  erro: string | null;
  cost_usd: number | null;
  // Camada 1 F2 — mapa completo (só populado no endpoint por-contrato; vazio no feed).
  // `dt_atualizacao` do CONTRATO no momento da leitura. Serve pro painel
  // pós-geração saber se a vistoria é DESTA geração — ver
  // `ValidacaoFidelidadePosGeracao`. Ausente em back antigo.
  contrato_dt_atualizacao?: string | null;
  clausulas?: IClausulaAuditada[];
  estrutura?: IEstruturaAuditada;
  // Fase 2 (issue #1158): parecer formato-issue (só no endpoint por-contrato;
  // null em relatórios sem JSON do contrato ou fail-open).
  parecer?: IParecerValidacao | null;
}

export interface IAuditoriaListaFilters {
  com_sinal?: boolean;
  limit?: number;
}

export const useAuditFidelidade = () => {
  // GET /api/v1/societario/auditoria-fidelidade?com_sinal=&limit=
  const getAuditoriaLista = async (key: QueryFunctionContext): Promise<IAuditoriaFidelidade[]> => {
    const filters = (key.queryKey?.[1] ?? {}) as IAuditoriaListaFilters;
    const res = await api.get<IDataNoPagination<IAuditoriaFidelidade[]>>(
      '/api/v1/societario/auditoria-fidelidade',
      { params: filters },
    );
    return res.data.data;
  };

  // GET /api/v1/societario/contratos/{id}/auditoria-fidelidade -> 1 relatorio ou null
  const getAuditoriaContrato = async (contratoId: number): Promise<IAuditoriaFidelidade | null> => {
    const res = await api.get<IDataNoPagination<IAuditoriaFidelidade | null>>(
      `/api/v1/societario/contratos/${contratoId}/auditoria-fidelidade`,
    );
    return res.data.data;
  };

  // POST .../auditoria-fidelidade/confirmar — Fase 1 GATE: confirma (revisa) a
  // vistoria daquele relatorio -> libera o download (back destranca o 409).
  // `auditoriaId` = relatorio que o operador viu; back rejeita (409) se surgiu
  // um mais novo (regeracao).
  const confirmarAuditoria = async (contratoId: number, auditoriaId: number): Promise<void> => {
    await api.post(`/api/v1/societario/contratos/${contratoId}/auditoria-fidelidade/confirmar`, {
      auditoria_id: auditoriaId,
    });
  };

  return { getAuditoriaLista, getAuditoriaContrato, confirmarAuditoria };
};
